import React, { useEffect, useRef, useState } from 'react'

import {
  arrayOf, bool, object, oneOfType, shape, string,
} from 'prop-types'

import usePageConfig from 'Pods/Shared/Hooks/usePageConfig'

import { EMPTY_ARRAY, EMPTY_OBJECT } from 'Utils/stableDefaults'

import MainFilters from './Components/MainFilters/MainFilters'
import Results from './Components/Results/Results'
import SecondaryFilters from './Components/SecondaryFilters/SecondaryFilters'
import SeoArea from './Components/SeoArea/SeoArea'
import useResultSetTracking from './Shared/Tracking/useResultSetTracking'
import { LocaleProvider } from './Shared/useTranslate'
import {
  pristineFilterState, searchPageStore, seedSearchPageStore, useSearchPageStore,
} from './Store'
import {
  cancelKeywordCommit, DEFAULT_PER_PAGE, fetchResults, throttledFetchResults,
} from './Store/actions'
import buildSearchParams from './Store/buildSearchParams'
import { ASSET_TYPE } from './Store/constants'
import hydrateStateFromUrl from './Store/hydrateStateFromUrl'
import { isPrintListingLocation } from './Store/listingBasePaths'
import normalizeMeta from './Store/normalizeMeta'

// Filters the URL path encodes rather than its query (see hydrateStateFromUrl): the print listing
// scope, the category scope, and the file type a software or extension page applies. The canonical
// URLs the fetches push keep the print scope in the path (GenerateListingPageUrl maps `printable` to
// /3d-print-models), so it is read from the location itself. A category page off the print listing
// (/3d-models/car) is 3D-only: the backend forces `print_ready: false` whenever a parent category
// is selected without the print scope (ItemsSearch::Options#adjust_print_ready_filter), so "All"
// would show a selection the SSR results never had. The software/extension file type has no query
// form hydrateStateFromUrl reads - it is echoed by the SSR params and applies only while the
// location is still the entry page it was seeded for.
const derivePathFilters = ({ printListing, categoryListing, fileTypes }) => {
  const pathFilters = {}
  if (printListing) {
    pathFilters.assetType = ASSET_TYPE.THREE_D_PRINT_ONLY
  } else if (categoryListing) {
    pathFilters.assetType = ASSET_TYPE.THREE_D_ONLY
  }
  if (fileTypes?.length) pathFilters.fileTypes = fileTypes
  return pathFilters
}

// params.fileTypes echoes every format the backend applied: the query's file_types plus the ones
// the software slug or the /ext/ segment inject (ExtractItemsSearchOptions#extract_file_types).
// Only the injected ones are path-derived; a query-selected format must not be re-applied by the
// popstate fallback once the restored URL no longer carries it.
const derivePathFileTypes = ({ url, params }) => {
  if (!params.fileTypes?.length) return undefined
  const queryFileTypes = hydrateStateFromUrl(url).fileTypes || []
  const pathFileTypes = params.fileTypes.map(String).filter((type) => !queryFileTypes.includes(type))
  return pathFileTypes.length ? pathFileTypes : undefined
}

// The entry page the path file types belong to. Software pages are their own pathname, but an
// extension page rewritten to `/3d-print-models?extensions=stl` shares its pathname with every
// canonical URL pushed afterwards, so the `extensions` param has to match as well.
const pathFileTypesEntry = ({ pathname, search }) => (
  `${pathname}|${new URLSearchParams(search).get('extensions') || ''}`
)

// Refetch only when the mapped backend params change. buildSearchParams already isolates the
// filter-relevant fields, so this ignores results/isLoading/page writes and never loops.
// Also owns popstate: every successful fetch pushes a history entry (Store/actions/results.js),
// so back/forward must restore the state the restored URL encodes - otherwise the address bar
// changes while filters and results stay frozen.
const subscribeToFilterAndHistoryChanges = ({ pathFileTypes }) => {
  let prevSignature = JSON.stringify(buildSearchParams(searchPageStore.getState()))
  const pathFileTypesEntryKey = pathFileTypes ? pathFileTypesEntry(window.location) : null
  let scheduledFetchId = 0
  let isFetchScheduled = false

  // Actions such as clearAllFilters write the store once per filter in the same tick. Fetching on
  // the first write would fire the throttle's leading call with a half-cleared search, render it
  // and report its result count; deferring to a microtask gives the whole burst one fetch.
  const scheduleFetch = () => {
    if (isFetchScheduled) {
      return
    }
    isFetchScheduled = true
    const fetchId = scheduledFetchId

    Promise.resolve().then(() => {
      isFetchScheduled = false
      if (fetchId === scheduledFetchId) {
        throttledFetchResults()
      }
    })
  }

  const cancelScheduledFetch = () => {
    scheduledFetchId += 1
    isFetchScheduled = false
  }

  const unsubscribeStore = searchPageStore.subscribe((state) => {
    const signature = JSON.stringify(buildSearchParams(state))
    if (signature !== prevSignature) {
      prevSignature = signature
      scheduleFetch()
    }
  })

  const handlePopstate = () => {
    cancelScheduledFetch()
    // A trailing throttled call scheduled by pre-Back filter changes would fire after the
    // restore with page 1 and URL pushes enabled, clobbering the restored entry - drop it.
    throttledFetchResults.cancel({ upcomingOnly: true })
    // Same for a keyword typed but not yet committed: the restore writes the keyword the URL
    // encodes, and a pending commit landing after it would re-apply the abandoned text.
    cancelKeywordCommit()
    const { pathname, search } = window.location
    // Every fetch sends the store's parent_category_slug (Store/actions/results.js), so the category
    // scope holds for the whole document regardless of which URL is restored.
    const pathFilters = derivePathFilters({
      printListing: isPrintListingLocation(),
      categoryListing: Boolean(searchPageStore.getState().parentCategorySlug),
      fileTypes: pathFileTypesEntry({ pathname, search }) === pathFileTypesEntryKey ? pathFileTypes : undefined,
    })
    // Reset to pristine defaults first so filters absent from the restored URL are cleared.
    const nextFilters = {
      ...pristineFilterState,
      ...hydrateStateFromUrl(pathname + search, pathFilters),
      // The restore usually writes the same keyword the store already holds, which never
      // rerenders the within-results input - so a draft typed before Back would stay visible
      // without ever applying. Bump the counter (instead of resetting it to the pristine 0)
      // so the input always resyncs, exactly like clearKeyword and setAssetType do.
      keywordClearCount: searchPageStore.getState().keywordClearCount + 1,
    }
    // Pre-align the signature: the setState below must not trigger the subscriber's own
    // (page-less, URL-pushing) fetch on top of the explicit one that follows.
    prevSignature = JSON.stringify(buildSearchParams(nextFilters))
    searchPageStore.setState(nextFilters)

    const urlParams = new URLSearchParams(search)
    fetchResults({
      page: Number(urlParams.get('page')) || 1,
      // Explicit default: falling back to meta.perPage would reuse the per-page of the entry
      // we just navigated AWAY from, not the one the restored URL encodes.
      perPage: Number(urlParams.get('per_page')) || DEFAULT_PER_PAGE,
      skipUrlUpdate: true,
    })
  }

  window.addEventListener('popstate', handlePopstate)

  return {
    // For store writes whose server result is already on screen (e.g. dropping actor-only filters
    // ItemsSearch ignored anyway): pre-align the signature exactly like the popstate restore does,
    // so the subscriber does not refetch identical results.
    applyStateWithoutRefetch: (partialState) => {
      prevSignature = JSON.stringify(buildSearchParams({ ...searchPageStore.getState(), ...partialState }))
      searchPageStore.setState(partialState)
    },
    unsubscribe: () => {
      // `upcomingOnly` clears the pending trailing call without latching the throttle's `cancelled`
      // flag - throttledFetchResults is a module singleton, so a bare cancel() would kill refetching
      // for every later mount (including StrictMode's mount -> unmount -> mount in dev).
      throttledFetchResults.cancel({ upcomingOnly: true })
      cancelScheduledFetch()
      unsubscribeStore()
      window.removeEventListener('popstate', handlePopstate)
    },
  }
}

// URL tokens carried by the shared licenses[] param that encode the actor-only filters below;
// stripped with replaceState so the address bar stops advertising filters a signed-out viewer
// cannot have, without spending a results fetch just to obtain the server's canonical URL.
const ACTOR_ONLY_LICENSE_TOKENS = ['wish_list_only', 'liked_models_only', 'hide_owned_models', 'owned_models']
// Legacy listing page tokens with no control here: hydration and the server search both drop
// them (ListingPageController::LEGACY_ONLY_LICENSES), so a shared legacy link must not keep them.
const LEGACY_ONLY_LICENSE_TOKENS = ['hide_owned_models']

const stripLicenseTokensFromUrl = (tokens) => {
  if (typeof window === 'undefined' || !window.history?.replaceState) {
    return
  }
  const { pathname, search } = window.location
  const urlParams = new URLSearchParams(search)
  let changed = false

  ;['licenses[]', 'licenses'].forEach((key) => {
    const values = urlParams.getAll(key)
    const kept = values.filter((value) => !tokens.includes(value))
    if (kept.length !== values.length) {
      changed = true
      urlParams.delete(key)
      kept.forEach((value) => urlParams.append(key, value))
    }
  })

  if (!changed) {
    return
  }
  const query = urlParams.toString()
  window.history.replaceState({}, document.title, query ? `${pathname}?${query}` : pathname)
}

function SearchPage({
  data = EMPTY_ARRAY,
  meta = EMPTY_OBJECT,
  filtersOptions = EMPTY_OBJECT,
  tags = EMPTY_ARRAY,
  url = '',
  params = EMPTY_OBJECT,
  isMobileSSR = false,
  shouldShowAllItemsOnMobile = false,
  browserAttributes = EMPTY_OBJECT,
  printModelsListing = false,
  paginationBaseUrl = '',
  itemListId = '',
  gaList = '',
}) {
  // Seed the store with SSR results synchronously on first render (server + client) so the
  // gallery is part of the prerendered markup and hydration does not shift layout. Filters from
  // the canonical URL are seeded too, so landing on a filtered link keeps those filters applied
  // (and reflected in the controls) instead of dropping them on the next fetch. The `url` prop is
  // used over window.location so server and client seed identically. The path-encoded filters
  // come from the echoed SSR params (window is absent on the server): without them the controls
  // would show defaults while the results are filtered, and the first fetch would silently drop
  // them. `params.printable` is the print scope the backend actually applied (path, print_ready
  // or printable param), not the request path: /3d-models?printable=1 stays on that URL when
  // new_url_structure is off, and /free-3d-print-models is a print listing under another segment.
  const [seeded] = useState(() => {
    const pathFileTypes = derivePathFileTypes({ url, params })
    const pathFilters = derivePathFilters({
      printListing: Boolean(params.printable),
      categoryListing: Boolean(params.parentCategorySlug),
      fileTypes: pathFileTypes,
    })

    seedSearchPageStore({
      ...hydrateStateFromUrl(url, pathFilters),
      parentCategorySlug: params.parentCategorySlug,
      subcategorySlug: params.subcategorySlug,
      canonicalUrl: '',
      results: data,
      meta: normalizeMeta(meta),
      filtersOptions,
      resultTags: tags,
      itemListId: itemListId || '',
      gaList: gaList || '',
    })
    return {
      // The page's own baseline: path-derived filters are part of it, so a print listing or a
      // software page does not count as "filtered" until the viewer changes something.
      signature: JSON.stringify(buildSearchParams(searchPageStore.getState())),
      pathFileTypes,
    }
  })

  useResultSetTracking()

  const applyStateWithoutRefetchRef = useRef(null)

  useEffect(() => {
    const { applyStateWithoutRefetch, unsubscribe } = subscribeToFilterAndHistoryChanges({
      pathFileTypes: seeded.pathFileTypes,
    })
    applyStateWithoutRefetchRef.current = applyStateWithoutRefetch
    stripLicenseTokensFromUrl(LEGACY_ONLY_LICENSE_TOKENS)
    return () => {
      applyStateWithoutRefetchRef.current = null
      unsubscribe()
    }
  }, [])

  const pageConfig = usePageConfig()

  // Actor-only filters hydrated from a shared logged-in URL (licenses[]=wish_list_only etc.) are
  // ignored by ItemsSearch without current_user, and the gated controls are hidden - leaving an
  // invisible "active" filter that every later request keeps re-sending. Once pageConfig confirms
  // a signed-out viewer, drop them without a refetch (the on-screen results already reflect the
  // token-less search) and clean the shared URL in place.
  useEffect(() => {
    if (!pageConfig || pageConfig.currentUserId) return
    const { personalFilter, purchaseFilter } = searchPageStore.getState()
    if (personalFilter === undefined && purchaseFilter === undefined) return
    applyStateWithoutRefetchRef.current?.({ personalFilter: undefined, purchaseFilter: undefined })
    stripLicenseTokensFromUrl(ACTOR_ONLY_LICENSE_TOKENS)
  }, [pageConfig])

  // The request params echoed by the backend are the only SSR-safe locale source: document is
  // absent during the fast-page SSR render, so reading <html lang> would always seed 'en' and
  // translate the server markup wrong on /de, /fr, ... (ListingPage precedent).
  const locale = params.locale || 'en'

  // The band belongs to the clean canonical category URL only. isTopBannerVisible carries that
  // check at SSR (signed out, no query params), but filters applied afterwards never revisit the
  // server props, so from then on the live store signature is what decides.
  const hasActiveFilters = useSearchPageStore(
    state => JSON.stringify(buildSearchParams(state)) !== seeded.signature
  )

  // Every remaining input is an SSR prop, so the band is part of the prerendered markup - gating
  // it on pageConfig instead would mount it after first paint and push the results grid down.
  const isSeoAreaVisible = Boolean(
    browserAttributes.isTopBannerVisible
    && browserAttributes.title
    && !browserAttributes.bottomDescription
    && !hasActiveFilters
  )

  return (
    <LocaleProvider locale={locale}>
      <div className="search-page">
        <div className="search-page__filters-container">
          <MainFilters />
          <SecondaryFilters />
        </div>
        {isSeoAreaVisible && (
          <SeoArea
            title={browserAttributes.title}
            description={browserAttributes.description}
            linkGroups={browserAttributes.seoLinkGroups}
            typeLinks={browserAttributes.validBrowserType}
            rootPath={browserAttributes.rootPath}
          />
        )}
        {/* Results also takes locale as a prop: it forwards it into the Triangle UI Context that
            ModelCard reads, which is a separate channel from the translation locale. */}
        <Results
          locale={locale}
          isMobileSSR={isMobileSSR}
          shouldShowAllItemsOnMobile={shouldShowAllItemsOnMobile}
          printModelsListing={printModelsListing}
          paginationBaseUrl={paginationBaseUrl}
        />
      </div>
    </LocaleProvider>
  )
}

SearchPage.propTypes = {
  data: arrayOf(object),
  meta: object,
  filtersOptions: object,
  tags: arrayOf(oneOfType([
    arrayOf(oneOfType([string, shape({})])),
    string,
  ])),
  url: string,
  params: object,
  isMobileSSR: bool,
  shouldShowAllItemsOnMobile: bool,
  browserAttributes: object,
  printModelsListing: bool,
  paginationBaseUrl: string,
  itemListId: string,
  gaList: string,
}

export default SearchPage
